import React, { memo } from 'react';
import type { BaseComponentProps, FormFieldProps } from '../../../types/common';

interface PhoneInputChangeEvent {
  target: {
    name: string;
    value: string;
  };
}

export interface PhoneInputProps extends BaseComponentProps, Omit<FormFieldProps, 'name'> {
  name: string;
  value: string;
  onChange: (event: PhoneInputChangeEvent) => void;
  placeholder?: string;
  maxLength?: number;
  pattern?: string;
  'aria-label'?: string;
  'aria-required'?: boolean;
}

const formatPhoneNumber = (input: string): string => {
  const cleaned = input.replace(/\D/g, '');

  if (cleaned.length >= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  if (cleaned.length > 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length > 3) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  }

  if (cleaned.length > 0) {
    return `(${cleaned}`;
  }

  return '';
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  value,
  onChange,
  placeholder = 'Phone number',
  maxLength = 14,
  pattern = '\\(\\d{3}\\) \\d{3}-\\d{4}',
  className = '',
  id,
  disabled = false,
  required = false,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
  'data-testid': testId,
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(event.target.value);
    onChange({
      target: {
        name,
        value: formattedValue,
      },
    });
  };

  const inputId = id || `phone-input-${name}`;

  return (
    <input
      type="tel"
      name={name}
      id={inputId}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      maxLength={maxLength}
      pattern={pattern}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel || placeholder}
      aria-required={ariaRequired}
      data-testid={testId}
      {...props}
    />
  );
};

export default memo(PhoneInput);
